import { useEffect, useState } from "react";
import { app, authentication } from "@microsoft/teams-js";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * useTeamsContext - silently attempts Teams SSO if the app is running inside
 * a Microsoft Teams iframe. No-op outside Teams.
 *
 * Returns { inTeams, ssoStatus: idle|trying|success|failed, error }
 */
export function useTeamsContext() {
  const { refresh } = useAuth();
  const [state, setState] = useState({ inTeams: false, ssoStatus: "idle", error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // app.initialize throws outside of Teams; bail silently.
        await app.initialize();
        const ctx = await app.getContext();
        if (cancelled) return;
        if (!ctx || !ctx.app) return;
        setState((s) => ({ ...s, inTeams: true, ssoStatus: "trying" }));
        try {
          const token = await authentication.getAuthToken();
          await api.post("/auth/teams-sso", { token });
          await refresh();
          if (!cancelled) setState({ inTeams: true, ssoStatus: "success", error: null });
        } catch (e) {
          if (!cancelled) {
            setState({
              inTeams: true,
              ssoStatus: "failed",
              error: formatApiError(e?.response?.data?.detail) || e?.message || String(e),
            });
          }
        }
      } catch (_) {
        // Not running inside Teams — ignore.
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
